import { useEffect, useState, useCallback, useRef } from "react";
import BrandLogo from "@/components/BrandLogo";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Radio, MapPin, Users, Play, Timer, Repeat, ListOrdered, Crown, Medal, Award,
  WifiOff, Loader2, Trophy, Clock, ScrollText,
} from "lucide-react";
import api from "@/lib/api";
import { REST_THRESHOLDS } from "@/lib/openPlayStore";
import ThemeToggle from "@/components/ThemeToggle";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SponsorTicker from "@/components/SponsorTicker";

function mmss(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function fmtSessionDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt || endedAt < startedAt) return "—";
  const totalMin = Math.round((endedAt - startedAt) / 60000);
  if (totalMin < 1) return "<1m";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const PODIUM = {
  1: { icon: Crown, color: "#facc15" },
  2: { icon: Medal, color: "#cbd5e1" },
  3: { icon: Award, color: "#f59e0b" },
};

function restHex(sinceMs, now) {
  const rest = (now - sinceMs) / 1000;
  if (rest < REST_THRESHOLDS.justFinished) return "#ef4444";
  if (rest < REST_THRESHOLDS.warmingUp) return "#eab308";
  return "#22c55e";
}

export default function LiveView() {
  const { token } = useParams();
  const [snap, setSnap] = useState(null);
  const [error, setError] = useState(false);
  const [now, setNow] = useState(Date.now());
  const hasData = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/openplay/live/${token}`);
      setSnap(data);
      hasData.current = true;
      setError(false);
    } catch (e) {
      if (!hasData.current) setError(true);
    }
  }, [token]);

  // poll the snapshot every 4s (independent from the clock)
  useEffect(() => {
    load();
    const poll = setInterval(load, 4000);
    return () => clearInterval(poll);
  }, [load]);

  // dedicated 1s clock so timers/rest colours tick live without a refresh
  useEffect(() => {
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(clock);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen cyber-grid flex flex-col items-center justify-center gap-4" style={{ background: "var(--bg)", color: "var(--text)" }}>
        <WifiOff className="w-10 h-10 text-primary-c" />
        <p className="font-display font-bold text-xl">Live session not found</p>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>Ask the organizer for the current link.</p>
      </div>
    );
  }
  if (!snap) {
    return (
      <div className="min-h-screen flex items-center justify-center cyber-grid" style={{ background: "var(--bg)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-primary-c" />
      </div>
    );
  }

  const playingCourts = (snap.courts || []).filter((c) => c.status === "in_use");
  const isEnded = !!snap.ended;
  const clockRef = isEnded && snap.ended_at ? snap.ended_at : now;

  return (
    <div className="min-h-screen grain" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <span className="font-display font-black tracking-tighter"><BrandLogo height={24} /></span>
          <span className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-primary-c">
            {isEnded ? (
              <span data-testid="live-final-badge" className="flex items-center gap-2" style={{ color: "var(--text-2)" }}>
                <Trophy className="w-3.5 h-3.5 text-primary-c" /> Final Results
              </span>
            ) : (
              <><span className="w-2 h-2 rounded-full pulse-ring" style={{ background: "var(--primary)" }} /> Live</>
            )}
          </span>
          <ThemeToggle />
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> Spectator View</span>
          <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-1 break-words">{snap.event_name || "Open Play"}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm" style={{ color: "var(--text-2)" }}>
            {snap.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {snap.location}</span>}
            <span className="capitalize">{snap.mode}</span>
            {snap.rotation && <span className="flex items-center gap-1 text-primary-c font-bold"><Repeat className="w-4 h-4" /> {snap.rotation}</span>}
            {!isEnded && <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {snap.counts?.checkedIn ?? 0} in · {snap.counts?.playing ?? 0} playing</span>}
          </div>
        </motion.div>

        {/* final results summary (shown when the session has ended) */}
        {isEnded && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-3 gap-3 mt-6" data-testid="live-final-summary">
            {[
              { label: "Matches", value: snap.counts?.matches ?? (snap.history || []).length, icon: ScrollText },
              { label: "Players", value: snap.counts?.participants ?? (snap.standings || []).length, icon: Users },
              { label: "Play time", value: fmtSessionDuration(snap.started_at, snap.ended_at), icon: Clock },
            ].map((c) => (
              <div key={c.label} className="rounded-2xl glow-border px-4 py-4 text-center" style={{ background: "var(--surface)" }}>
                <c.icon className="w-5 h-5 mx-auto text-primary-c mb-1.5" />
                <div className="font-display font-bold text-2xl">{c.value}</div>
                <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{c.label}</div>
              </div>
            ))}
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-8">
          {/* courts */}
          <div className="lg:col-span-8">
            {!isEnded && (
              <>
                <h3 className="font-display font-bold flex items-center gap-2 text-xl mb-4"><Timer className="w-5 h-5 text-primary-c" /> Now Playing</h3>
                {playingCourts.length === 0 ? (
                  <div className="rounded-2xl glow-border p-8 text-center text-sm" style={{ background: "var(--surface)", color: "var(--text-2)" }}>No matches on court right now.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {playingCourts.map((c, i) => {
                      const elapsed = c.startTime ? (clockRef - c.startTime) / 1000 : 0;
                      return (
                        <div key={i} className="rounded-2xl tracing-beam glow-border p-5" style={{ background: "var(--surface)" }}>
                          <div className="flex items-center justify-between">
                            <span className="font-display font-bold">{c.name}</span>
                            <span className="mono font-bold text-2xl text-primary-c">{mmss(elapsed)}</span>
                          </div>
                          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 mt-3">
                            <div className="min-w-0">{(c.teamA || []).map((n, k) => <div key={k} className="font-semibold truncate">{n}</div>)}</div>
                            <span className="mono text-xs shrink-0" style={{ color: "var(--text-2)" }}>VS</span>
                            <div className="text-right min-w-0">{(c.teamB || []).map((n, k) => <div key={k} className="font-semibold truncate">{n}</div>)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* results */}
            {(snap.history || []).length > 0 && (
              <div className="mt-8">
                <h3 className="font-display font-bold flex items-center gap-2 text-xl mb-4"><Trophy className="w-5 h-5 text-primary-c" /> {isEnded ? `Match Log (${snap.history.length})` : "Recent Results"}</h3>
                <div className="rounded-2xl glow-border divide-y max-h-[520px] overflow-y-auto" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="live-match-log">
                  {(isEnded ? snap.history : snap.history.slice(0, 12)).map((m, i) => {
                    const hasScore = m.score_a != null && m.score_b != null;
                    return (
                      <div key={i} data-testid="live-match-row" className="px-4 py-3 text-sm flex items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
                        <span className="truncate">{(m.team_a || []).join(" & ")} <span style={{ color: "var(--text-2)" }}>vs</span> {(m.team_b || []).join(" & ")}</span>
                        <span className="shrink-0 mono text-xs text-primary-c flex items-center gap-2">
                          {hasScore && <span style={{ color: "var(--text-2)" }}>{m.score_a}–{m.score_b}</span>}
                          {m.winners?.length ? <span className="flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {m.winners.join(" & ")}</span> : "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* participants (final results) */}
            {isEnded && (snap.participants || []).length > 0 && (
              <div className="mt-8">
                <h3 className="font-display font-bold flex items-center gap-2 text-xl mb-4"><Users className="w-5 h-5 text-primary-c" /> Players ({snap.participants.length})</h3>
                <div className="rounded-2xl glow-border p-4 flex flex-wrap gap-2" style={{ background: "var(--surface)" }} data-testid="live-participants">
                  {snap.participants.map((p, i) => (
                    <span key={i} data-testid="live-participant" className="mono text-xs rounded-full px-2.5 py-1"
                      style={p.checked_out ? { background: "rgba(234,179,8,0.14)", color: "#eab308" } : { background: "var(--bg)", color: "var(--text)" }}>
                      {p.name}{p.checked_out ? " (out)" : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* queue + standings */}
          <div className="lg:col-span-4 space-y-6">
            {!isEnded && (
            <div className="rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }}>
              <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Repeat className="w-5 h-5 text-primary-c" /> Up Next <span className="mono text-xs glass rounded-full px-2 py-0.5">{(snap.queue || []).length}</span></h3>
              {(snap.queue || []).length === 0 ? (
                <p className="text-sm py-3 text-center" style={{ color: "var(--text-2)" }}>Queue is empty.</p>
              ) : (
                <div className="space-y-2">
                  {snap.queue.map((q, i) => {
                    const hex = restHex(q.since || clockRef, clockRef);
                    return (
                      <div key={i} className="flex items-center justify-between rounded-xl px-3 py-2" style={{ background: "var(--bg)", borderLeft: `3px solid ${hex}` }}>
                        <span className="flex items-center gap-3"><span className="mono text-xs w-5 text-primary-c">{i + 1}</span>{q.name}</span>
                        <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{mmss((clockRef - (q.since || clockRef)) / 1000)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {(snap.show_standings !== false || isEnded) && (
            <div className="rounded-2xl glow-border p-6" style={{ background: "var(--surface)" }} data-testid="live-standings">
              <h3 className="font-display font-bold flex items-center gap-2 mb-4"><ListOrdered className="w-5 h-5 text-primary-c" /> Standings</h3>
              {(snap.standings || []).length === 0 ? (
                <p className="text-sm py-3 text-center" style={{ color: "var(--text-2)" }}>No players yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {(() => { let rc = 0; return snap.standings.map((p, i) => {
                    const rank = p.provisional ? null : ++rc;
                    const podium = rank ? PODIUM[rank] : null;
                    return (
                      <div key={i} data-testid={p.provisional ? `live-standing-provisional-row-${i}` : `live-standing-row-${rank}`} className="flex items-center justify-between rounded-xl px-3 py-2 text-sm" style={{ background: "var(--bg)", border: podium ? `1px solid ${podium.color}` : "1px solid transparent", opacity: p.provisional ? 0.55 : 1 }}>
                        <span className="flex items-center gap-3 min-w-0">
                          <span className="w-6 flex justify-center shrink-0">{podium ? <podium.icon className="w-4 h-4" style={{ color: podium.color }} /> : <span className="mono text-xs" style={{ color: "var(--text-2)" }}>{rank || "—"}</span>}</span>
                          <span className="min-w-0">
                            <span className="font-semibold flex items-center gap-2 truncate" style={podium ? { color: podium.color } : {}}>
                              {p.name}
                              {p.tie_note && !p.provisional && (
                                <Popover>
                                  <PopoverTrigger asChild><button data-testid={`live-tie-info-${rank}`} aria-label="Why this rank?" className="shrink-0" style={{ color: "#eab308" }}>ⓘ</button></PopoverTrigger>
                                  <PopoverContent className="glass w-72 text-xs" style={{ background: "var(--surface-glass)" }}>{p.tie_note}</PopoverContent>
                                </Popover>
                              )}
                              {p.tag && !p.provisional && <span className="mono text-[9px] uppercase tracking-wider glass rounded-full px-1.5 py-0.5 text-primary-c shrink-0">{p.tag}</span>}
                              {p.provisional && <span data-testid={`live-standing-provisional-${i}`} className="mono text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 shrink-0" style={{ background: "rgba(234,179,8,0.16)", color: "#eab308" }}>needs {p.needs} more game{p.needs === 1 ? "" : "s"}</span>}
                            </span>
                            {(p.metric_primary || p.metric_secondary) && (
                              <span className="mono text-[10px]" style={{ color: "var(--text-2)" }}>{p.metric_primary}{p.metric_secondary ? ` · ${p.metric_secondary}` : ""}</span>
                            )}
                          </span>
                        </span>
                        <span className="mono text-xs shrink-0"><span className="text-primary-c font-bold">{p.wins}W</span> <span style={{ color: "var(--text-2)" }}>{p.games}g</span></span>
                      </div>
                    );
                  }); })()}
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs mt-10 mono" style={{ color: "var(--text-2)" }}>Auto-refreshing · Powered by PointControl</p>
      </div>

      <SponsorTicker />
    </div>
  );
}
